import { useState } from 'react';
import { Link } from 'react-router-dom';
import SEOHead from '@/components/base/SEOHead';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import Breadcrumb from '@/components/base/Breadcrumb';
import { reputationData, testimonials } from '@/mocks/testimonials';

export default function AboutPage() {
  const [testimonialIndex, setTestimonialIndex] = useState(0);
  const t = testimonials[testimonialIndex];

  const goTo = (dir: number) => {
    setTestimonialIndex((prev) => (prev + dir + testimonials.length) % testimonials.length);
  };

  const aboutJsonLd = {
    '@context': 'https://schema.org',
    '@type': 'AboutPage',
    name: '关于酷漫屋 - 国内领先的免费漫画阅读平台',
    description: '酷漫屋品牌介绍、发展历程、用户口碑和联系方式。成立于2018年，专注优质韩漫、国漫在线观看。',
    url: 'http://lst56.com/about',
    isPartOf: {
      '@type': 'WebSite',
      name: '酷漫屋',
      url: 'http://lst56.com/',
    },
  };

  const orgJsonLd = {
    '@context': 'https://schema.org',
    '@type': 'Organization',
    name: '酷漫屋',
    url: 'http://lst56.com/',
    foundingDate: '2018',
    description: '国内领先的免费漫画阅读平台，专注提供优质韩漫、国漫等各类漫画资源。累计用户5800万+，漫画作品12万+。',
    address: {
      '@type': 'PostalAddress',
      addressCountry: 'CN',
      addressLocality: '北京',
      addressRegion: '北京市',
    },
    contactPoint: {
      '@type': 'ContactPoint',
      contactType: 'customer service',
      email: 'hello@lst56.com',
      availableLanguage: ['Chinese'],
    },
    sameAs: ['http://lst56.com/'],
  };

  const faqJsonLd = {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    mainEntity: [
      {
        '@type': 'Question',
        name: '酷漫屋的漫画是免费的吗？',
        acceptedAnswer: {
          '@type': 'Answer',
          text: '是的，酷漫屋所有漫画均完全免费。我们提供下拉式阅读体验，无需注册登录即可畅享海量漫画资源。',
        },
      },
      {
        '@type': 'Question',
        name: '酷漫屋的漫画多久更新一次？',
        acceptedAnswer: {
          '@type': 'Answer',
          text: '酷漫屋每日更新超过300个新章节，热门漫画保持与原作者同步更新。您可以通过订阅邮件通知获取最新更新信息。',
        },
      },
      {
        '@type': 'Question',
        name: '如何在酷漫屋找到喜欢的漫画？',
        acceptedAnswer: {
          '@type': 'Answer',
          text: '您可以通过首页的分类导航（韩漫、国漫、少女、热血等）浏览不同类型的漫画。也可以使用搜索功能输入漫画名称或作者名快速找到想看的作品。',
        },
      },
      {
        '@type': 'Question',
        name: '酷漫屋支持哪些设备阅读？',
        acceptedAnswer: {
          '@type': 'Answer',
          text: '酷漫屋采用响应式设计，完美适配电脑、平板和手机等各类设备。无论您使用什么设备，都能获得流畅的下拉式阅读体验。',
        },
      },
    ],
  };

  const breadcrumbJsonLd = {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: [
      { '@type': 'ListItem', position: 1, name: '首页', item: 'http://lst56.com/' },
      { '@type': 'ListItem', position: 2, name: '关于我们' },
    ],
  };

  return (
    <>
      <SEOHead
        title="关于酷漫屋 - 国内领先的免费漫画阅读平台 | 品牌介绍"
        description="酷漫屋成立于2018年，是国内领先的免费漫画阅读平台。累计用户5800万+，提供海量韩漫、国漫等各类漫画资源。下拉式阅读，高清画质，永久免费。"
        keywords="酷漫屋,关于酷漫屋,免费漫画平台,漫画阅读,品牌介绍"
        canonicalUrl="http://lst56.com/about"
        jsonLd={aboutJsonLd}
      />
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(orgJsonLd) }}
      />
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(faqJsonLd) }}
      />
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(breadcrumbJsonLd) }}
      />

      <div className="min-h-screen bg-background-50">
        <Navbar />
        <main>
          {/* Brand Story */}
          <section className="relative overflow-hidden" style={{ background: 'linear-gradient(135deg, #3d2b1f 0%, #2a1a10 40%, #1a0f08 100%)' }}>
            <div className="max-w-7xl mx-auto px-4 md:px-6 py-16 md:py-24">
              <div className="flex flex-col lg:flex-row gap-10 md:gap-16 items-start">
                {/* Left: Brand image */}
                <div className="flex-shrink-0 mx-auto lg:mx-0">
                  <div className="w-64 md:w-72 rounded-xl overflow-hidden" style={{ backgroundColor: 'rgba(255,255,255,0.1)' }}>
                    <img
                      src="https://readdy.ai/api/search-image?query=A%20warm%20and%20inviting%20creative%20workspace%20with%20webtoon%20art%20on%20walls%2C%20bookshelves%20filled%20with%20comics%2C%20warm%20amber%20lighting%2C%20cozy%20Korean%20manhwa%20studio%20aesthetic%2C%20clean%20minimal%20design%2C%20professional%20creative%20environment%2C%20soft%20natural%20light%2C%20artistic%20atmosphere&width=600&height=600&seq=about-brand-office&orientation=squarish"
                      alt="酷漫屋品牌工作室"
                      className="w-full aspect-square object-cover"
                    />
                  </div>
                </div>

                {/* Right: Content */}
                <div className="flex-1 flex flex-col gap-5">
                  <div>
                    <span className="inline-block px-3 py-1 rounded-full text-xs font-medium" style={{ backgroundColor: 'rgba(255,180,180,0.2)', color: '#f0c0c0' }}>
                      /关于/
                    </span>
                  </div>
                  <h1 className="text-3xl md:text-4xl lg:text-5xl font-bold text-white leading-tight" style={{ fontFamily: "var(--font-heading)" }}>
                    酷漫屋品牌故事
                  </h1>
                  <p className="text-sm md:text-base leading-relaxed" style={{ color: 'rgba(255,255,255,0.55)' }}>
                    酷漫屋成立于2018年，由一群热爱漫画的年轻人创立。我们看到了大量中文读者对优质漫画——尤其是韩国漫画——的强烈需求，但市场上缺乏一个真正免费、高清、体验流畅的阅读平台。于是，酷漫屋应运而生。
                  </p>
                  <p className="text-sm md:text-base leading-relaxed" style={{ color: 'rgba(255,255,255,0.45)' }}>
                    六年来，我们始终坚持"漫画，本该免费"的理念，累计服务超过5800万用户，收录超过12万部漫画作品。从韩漫到国漫，从热血战斗到浪漫少女，从悬疑推理到搞笑日常——酷漫屋致力于让每一位漫迷都能在这里找到属于自己的快乐。
                  </p>

                  <div className="flex flex-col gap-2 pt-2">
                    <div className="flex items-baseline gap-3">
                      <span className="text-5xl md:text-6xl font-black text-white" style={{ fontFamily: "var(--font-heading)" }}>
                        2018
                      </span>
                      <span className="text-sm" style={{ color: 'rgba(255,255,255,0.4)' }}>年创立</span>
                    </div>
                  </div>

                  <a
                    href="mailto:hello@lst56.com"
                    className="inline-flex items-center gap-2 px-6 py-3 rounded-full bg-background-50 text-foreground-950 text-sm font-semibold hover:bg-background-100 transition-all duration-300 whitespace-nowrap cursor-pointer w-fit"
                  >
                    联系我们
                    <i className="ri-mail-send-line text-base"></i>
                  </a>
                </div>
              </div>
            </div>
          </section>

          <Breadcrumb
            items={[
              { label: '首页', href: '/' },
              { label: '关于我们' },
            ]}
          />

          {/* Stats / Reputation */}
          <section className="py-16 md:py-20 bg-background-50">
            <div className="max-w-7xl mx-auto px-4 md:px-6">
              <div className="text-center mb-12">
                <h2 className="text-2xl md:text-3xl font-bold text-foreground-950" style={{ fontFamily: "var(--font-heading)" }}>
                  平台<span className="text-accent-500">数据</span>
                </h2>
                <p className="text-sm md:text-base text-foreground-500 mt-3">
                  用数据说话，每一位用户都是我们的骄傲
                </p>
              </div>

              <div className="grid grid-cols-2 md:grid-cols-4 gap-6 md:gap-8">
                {reputationData.map((item) => (
                  <div key={item.label} className="flex flex-col items-center gap-3 p-6 rounded-xl bg-background-100">
                    <div className="w-14 h-14 rounded-full bg-accent-100 flex items-center justify-center">
                      <i className={`${item.icon} text-accent-600 text-xl w-5 h-5 flex items-center justify-center`}></i>
                    </div>
                    <span className="text-2xl md:text-3xl font-bold text-foreground-950">{item.value}</span>
                    <span className="text-xs md:text-sm text-foreground-500">{item.label}</span>
                  </div>
                ))}
              </div>
            </div>
          </section>

          {/* Testimonials */}
          <section className="py-16 md:py-20 bg-background-100">
            <div className="max-w-7xl mx-auto px-4 md:px-6">
              <div className="flex flex-col lg:flex-row gap-8 md:gap-12 items-center">
                <div className="w-full lg:w-[40%]">
                  <div className="relative w-64 h-64 md:w-80 md:h-80 mx-auto rounded-3xl overflow-hidden bg-accent-100">
                    <img src={t.avatar} alt={t.name} className="w-full h-full object-cover" />
                  </div>
                </div>
                <div className="w-full lg:w-[60%] flex flex-col gap-6">
                  <span className="text-xs md:text-sm text-foreground-400 tracking-wider">（行业口碑）</span>
                  <h2 className="text-2xl md:text-3xl lg:text-4xl font-bold text-foreground-950 leading-tight" style={{ fontFamily: "var(--font-heading)" }}>
                    用户口碑<br />
                    <span className="text-foreground-300">是最好的证明</span>
                  </h2>
                  <div className="relative">
                    <i className="ri-double-quotes-l text-4xl text-accent-200 absolute -top-3 -left-1"></i>
                    <p className="text-base md:text-lg text-foreground-600 leading-relaxed pl-8">{t.quote}</p>
                  </div>
                  <div className="flex items-center gap-3">
                    <span className="text-foreground-300">—</span>
                    <div>
                      <p className="text-sm font-semibold text-foreground-900">{t.name}</p>
                      <p className="text-xs text-foreground-500">{t.role}</p>
                    </div>
                  </div>
                  <div className="flex gap-1">
                    {Array.from({ length: t.rating }).map((_, i) => (
                      <i key={i} className="ri-star-fill text-accent-500 text-sm w-4 h-4 flex items-center justify-center"></i>
                    ))}
                  </div>
                  <div className="flex gap-3 mt-2">
                    <button
                      onClick={() => goTo(-1)}
                      className="w-10 h-10 rounded-full border border-foreground-200 flex items-center justify-center hover:border-foreground-400 transition-colors duration-200 cursor-pointer"
                    >
                      <i className="ri-arrow-left-line text-foreground-600 text-lg"></i>
                    </button>
                    <button
                      onClick={() => goTo(1)}
                      className="w-10 h-10 rounded-full bg-foreground-950 flex items-center justify-center hover:bg-foreground-800 transition-colors duration-200 cursor-pointer"
                    >
                      <i className="ri-arrow-right-line text-background-50 text-lg"></i>
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </section>

          {/* FAQ */}
          <section className="py-16 md:py-20 bg-background-50">
            <div className="max-w-3xl mx-auto px-4 md:px-6">
              <h2 className="text-2xl md:text-3xl font-bold text-foreground-950 mb-10 text-center" style={{ fontFamily: "var(--font-heading)" }}>
                常见<span className="text-accent-500">问题</span>
              </h2>
              <div className="space-y-4">
                {[
                  { q: '酷漫屋的漫画是免费的吗？', a: '是的，酷漫屋所有漫画均完全免费。我们提供下拉式阅读体验，无需注册登录即可畅享海量漫画资源。' },
                  { q: '酷漫屋的漫画多久更新一次？', a: '酷漫屋每日更新超过300个新章节，热门漫画保持与原作者同步更新。您可以通过订阅邮件通知获取最新更新信息。' },
                  { q: '如何在酷漫屋找到喜欢的漫画？', a: '您可以通过首页的分类导航（韩漫、国漫、少女、热血等）浏览不同类型的漫画。也可以使用搜索功能输入漫画名称或作者名快速找到想看的作品。' },
                  { q: '酷漫屋支持哪些设备阅读？', a: '酷漫屋采用响应式设计，完美适配电脑、平板和手机等各类设备。无论您使用什么设备，都能获得流畅的下拉式阅读体验。' },
                ].map((faq, i) => (
                  <details key={i} className="group border border-background-200 rounded-xl overflow-hidden">
                    <summary className="flex items-center justify-between px-5 py-4 cursor-pointer hover:bg-background-100 transition-colors duration-200">
                      <span className="text-sm md:text-base font-medium text-foreground-800 pr-4">{faq.q}</span>
                      <i className="ri-arrow-down-s-line text-foreground-400 group-open:rotate-180 transition-transform duration-200 text-lg w-5 h-5 flex items-center justify-center flex-shrink-0"></i>
                    </summary>
                    <div className="px-5 pb-4">
                      <p className="text-sm text-foreground-600 leading-relaxed">{faq.a}</p>
                    </div>
                  </details>
                ))}
              </div>
            </div>
          </section>

          {/* Contact */}
          <section className="py-16 md:py-20 bg-background-100">
            <div className="max-w-3xl mx-auto px-4 md:px-6 text-center">
              <h2 className="text-2xl md:text-3xl font-bold text-foreground-950 mb-4" style={{ fontFamily: "var(--font-heading)" }}>
                商务<span className="text-accent-500">合作</span>
              </h2>
              <p className="text-sm md:text-base text-foreground-500 mb-8">
                如果您有任何合作意向、广告投放需求或意见建议，欢迎通过以下方式联系我们
              </p>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-8">
                <a
                  href="mailto:hello@lst56.com"
                  className="flex items-center gap-3 p-4 rounded-xl bg-background-50 hover:bg-accent-50 transition-colors duration-200 cursor-pointer"
                >
                  <div className="w-10 h-10 rounded-full bg-accent-100 flex items-center justify-center flex-shrink-0">
                    <i className="ri-mail-line text-accent-600 text-lg w-4 h-4 flex items-center justify-center"></i>
                  </div>
                  <div className="text-left">
                    <p className="text-sm font-semibold text-foreground-800">电子邮件</p>
                    <p className="text-xs text-foreground-500">hello@lst56.com</p>
                  </div>
                </a>
                <div className="flex items-center gap-3 p-4 rounded-xl bg-background-50 cursor-default">
                  <div className="w-10 h-10 rounded-full bg-accent-100 flex items-center justify-center flex-shrink-0">
                    <i className="ri-map-pin-line text-accent-600 text-lg w-4 h-4 flex items-center justify-center"></i>
                  </div>
                  <div className="text-left">
                    <p className="text-sm font-semibold text-foreground-800">公司地址</p>
                    <p className="text-xs text-foreground-500">北京市朝阳区创意产业园</p>
                  </div>
                </div>
              </div>

              <a
                href="mailto:hello@lst56.com"
                className="inline-flex items-center gap-2 px-6 py-3 rounded-full bg-foreground-950 text-background-50 text-sm font-semibold hover:bg-foreground-800 transition-all duration-300 whitespace-nowrap cursor-pointer"
              >
                发送邮件
                <i className="ri-arrow-right-line text-base"></i>
              </a>
            </div>
          </section>
        </main>
        <Footer />
      </div>
    </>
  );
}